import { useState, useEffect, forwardRef, useImperativeHandle } from "react";
import SignatorySelector from "./SignatorySelector";
import SignatureUploader from "./SignatureUploader";
import { api } from "../../../../services/api/api";

interface SignatureEditFormProps {
  type: "BEFORE" | "AFTER";
  workorderId: string;
  contacts: EasyTech.Contact[];
  canEdit: boolean;
  workorder: any;
  updateWorkorder: (w: any) => void;
  signatures: EasyTech.Photo[];
  setSignatures: (fn: (prev: EasyTech.Photo[]) => EasyTech.Photo[]) => void;
  deleteSignature: (id: string) => Promise<void>;
  getDownloadUrl: (id: string) => string;
  onCancel: () => void;
  onSaved: () => void;
  localImage: string;
  setLocalImage: (s: string) => void;
  pendingFile: File | null;
  setPendingFile: (f: File | null) => void;
}

const SignatureEditForm = forwardRef(function SignatureEditForm({
  type,
  workorderId,
  contacts,
  canEdit,
  workorder,
  updateWorkorder,
  signatures,
  setSignatures,
  onSaved,
  localImage,
  setLocalImage,
  pendingFile,
  setPendingFile
}: SignatureEditFormProps, ref) {
  const [manualMode, setManualMode] = useState(false);
  const [manualFields, setManualFields] = useState({ firstname: "", lastname: "", function: "" });
  const [selectedContact, setSelectedContact] = useState<EasyTech.Contact | null>(contacts[0] || null);
  const [isOnsiteSelected, setIsOnsiteSelected] = useState(false);
  const [initialOnsiteFields, setInitialOnsiteFields] = useState({ firstname: "", lastname: "", function: "" });

  const currentSignature = signatures.find(sig => sig.type === type);

  useImperativeHandle(ref, () => ({
    save: handleSave
  }));

  useEffect(() => {
    if (workorder?.onSiteContact?.name) {
      const nameParts = workorder.onSiteContact.name.split(" ");
      const onsiteFields = {
        firstname: nameParts[0] || "",
        lastname: nameParts.slice(1).join(" ") || "",
        function: ""
      };
      setInitialOnsiteFields(onsiteFields);
      
      // Si aucun contact n'est sélectionné, utiliser le contact onsite par défaut
      if (!selectedContact && !manualMode) {
        setManualFields(onsiteFields);
      }
    }
  }, [workorder?.onSiteContact, selectedContact, manualMode]);

  // Vérifier si les champs onsite ont été modifiés
  const areOnsiteFieldsModified = () => {
    return (
      manualFields.firstname !== initialOnsiteFields.firstname ||
      manualFields.lastname !== initialOnsiteFields.lastname ||
      manualFields.function !== initialOnsiteFields.function
    );
  };

  async function handleSave() {
    try {
      // Mettre à jour le contact On Site si nécessaire (uniquement si sélectionné et modifié)
      if (isOnsiteSelected && areOnsiteFieldsModified()) {
        const name = `${manualFields.firstname} ${manualFields.lastname}`;
        await api.put(
          `users/workorders/${workorderId}/onsite`,
          { name, phone: workorder?.onSiteContact?.phone || "" }
        );
        if (workorder && updateWorkorder) {
          updateWorkorder({
            ...workorder,
            onSiteContact: {
              ...workorder.onSiteContact,
              name,
              phone: workorder?.onSiteContact?.phone || ""
            }
          });
        }
      }

      const signatureData = new FormData();
      
      if (manualMode) {
        signatureData.append("firstname", manualFields.firstname);
        signatureData.append("lastname", manualFields.lastname);
        signatureData.append("function", manualFields.function);
      } else if (isOnsiteSelected) {
        signatureData.append("firstname", manualFields.firstname);
        signatureData.append("lastname", manualFields.lastname);
        signatureData.append("function", manualFields.function);
      } else if (selectedContact) {
        signatureData.append("contact", selectedContact.id);
        signatureData.append("firstname", selectedContact.firstName);
        signatureData.append("lastname", selectedContact.lastName);
        signatureData.append("function", selectedContact.function || "");
      }

      // Ajouter l'image si elle existe
      if (pendingFile) {
        signatureData.append("photo", pendingFile);
      }

      // Créer ou mettre à jour la signature
      let newSignature: EasyTech.Photo | null = null;
      
      if (pendingFile) {
        // Si une nouvelle image est uploadée, toujours utiliser POST avec le type
        newSignature = await api.post(
          `users/workorders/${workorderId}/signatures/${type.toLowerCase()}`,
          signatureData,
          { isMultipart: true }
        ) as EasyTech.Photo;
        
        // Mettre à jour l'état des signatures
        if (newSignature) {
          setSignatures(prev => {
            const filtered = prev.filter(sig => sig.type !== type);
            return [...filtered, newSignature as EasyTech.Photo];
          });
        }
      } else if (currentSignature) {
        // Si pas de nouvelle image, mais signature existante à mettre à jour
        newSignature = await api.put(
          `users/workorders/${workorderId}/signatures/${currentSignature.id}`,
          signatureData,
          { isMultipart: true }
        ) as EasyTech.Photo;
        
        // Mettre à jour l'état des signatures après la mise à jour
        if (newSignature) {
          setSignatures(prev => {
            const filtered = prev.filter(sig => sig.id !== currentSignature.id);
            return [...filtered, newSignature as EasyTech.Photo];
          });
        }
      } else {
        if (manualMode || isOnsiteSelected || selectedContact) {
          newSignature = await api.post(
            `users/workorders/${workorderId}/signatures/${type.toLowerCase()}`,
            signatureData,
            { isMultipart: true }
          ) as EasyTech.Photo;
          
          // Mettre à jour l'état des signatures
          if (newSignature) {
            setSignatures(prev => {
              const filtered = prev.filter(sig => sig.type !== type);
              return [...filtered, newSignature as EasyTech.Photo];
            });
          }
        }
      }
      
      setLocalImage("");
      setPendingFile(null);
      onSaved();
    } catch (e) {
      console.error("Error saving signature:", e);
    }
  }

  return (
    <div className="mb-4">
      <SignatorySelector
        contacts={contacts}
        manualMode={manualMode}
        setManualMode={setManualMode}
        manualFields={manualFields}
        setManualFields={setManualFields}
        selectedContact={selectedContact}
        setSelectedContact={setSelectedContact}
        canEdit={canEdit}
        workorder={workorder}
        isOnsiteSelected={isOnsiteSelected}
        setIsOnsiteSelected={setIsOnsiteSelected}
        initialOnsiteFields={initialOnsiteFields}
      />
      <SignatureUploader
        localImage={localImage}
        setLocalImage={setLocalImage}
        pendingFile={pendingFile}
        setPendingFile={setPendingFile}
        canEdit={canEdit}
      />
    </div>
  );
});

export default SignatureEditForm;